import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { postsService as postService } from '../services/service-proxy';
import { useAuth } from './auth.context';

const PostsContext = createContext();

const PostsProvider = ({ children }) => {
  const { user, profile, isAuthenticated } = useAuth();

  // Posts feed state (legacy - kept for backward compatibility with components
  // that read posts from context. Pages now use useFeed hook for their own feeds.)
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Active post type filter for the feed (null = all types)
  const [activePostTypeFilter, setActivePostTypeFilter] = useState(null);

  // Pagination cursor - store the last Firestore doc snapshot
  const lastDocRef = useRef(null);

  // Like state cache: { [postId]: boolean }
  const [likedPosts, setLikedPosts] = useState({});

  // Callbacks for notifying feed pages when a post is created
  const postCreatedListenersRef = useRef([]);

  // New post dialog state
  const [newPostOpen, setNewPostOpen] = useState(false);

  // Workout post dialog state
  const [workoutPostOpen, setWorkoutPostOpen] = useState(false);

  // Meal post dialog state
  const [mealPostOpen, setMealPostOpen] = useState(false);

  // Progress post dialog state
  const [progressPostOpen, setProgressPostOpen] = useState(false);

  const openNewPost = useCallback(() => setNewPostOpen(true), []);
  const closeNewPost = useCallback(() => setNewPostOpen(false), []);

  const openWorkoutPost = useCallback(() => setWorkoutPostOpen(true), []);
  const closeWorkoutPost = useCallback(() => setWorkoutPostOpen(false), []);

  const openMealPost = useCallback(() => setMealPostOpen(true), []);
  const closeMealPost = useCallback(() => setMealPostOpen(false), []);

  const openProgressPost = useCallback(() => setProgressPostOpen(true), []);
  const closeProgressPost = useCallback(() => setProgressPostOpen(false), []);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Register a listener for post creation events.
   * Used by feed pages (Home, Explore) to prepend new posts.
   * @param {function} listener - Callback receiving the new post object.
   * @returns {function} Unsubscribe function.
   */
  const onPostCreated = useCallback((listener) => {
    postCreatedListenersRef.current.push(listener);
    return () => {
      postCreatedListenersRef.current = postCreatedListenersRef.current.filter(
        (l) => l !== listener
      );
    };
  }, []);

  /**
   * Fetch the first page of posts (initial load or refresh).
   * Optionally filter by post type.
   * @param {string|null} typeFilter - Post type to filter by, or null for all.
   */
  const fetchPosts = useCallback(async (typeFilter = null) => {
    setLoading(true);
    setError(null);
    try {
      let result;
      if (typeFilter) {
        result = await postService.getPostsByType(typeFilter, null, 10);
      } else {
        result = await postService.getPosts(null, 10);
      }
      setPosts(result.posts);
      lastDocRef.current = result.lastDoc;
      setHasMore(result.hasMore);

      // Check like status for each post
      if (user) {
        const likeChecks = {};
        await Promise.all(
          result.posts.map(async (post) => {
            const liked = await postService.checkIfLiked(post.id, user.uid);
            likeChecks[post.id] = liked;
          })
        );
        setLikedPosts((prev) => ({ ...prev, ...likeChecks }));
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [user]);

  /**
   * Fetch the next page of posts for infinite scroll.
   */
  const fetchMorePosts = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    setError(null);
    try {
      let result;
      if (activePostTypeFilter) {
        result = await postService.getPostsByType(activePostTypeFilter, lastDocRef.current, 10);
      } else {
        result = await postService.getPosts(lastDocRef.current, 10);
      }
      setPosts((prev) => [...prev, ...result.posts]);
      lastDocRef.current = result.lastDoc;
      setHasMore(result.hasMore);

      // Check like status for new posts
      if (user) {
        const likeChecks = {};
        await Promise.all(
          result.posts.map(async (post) => {
            const liked = await postService.checkIfLiked(post.id, user.uid);
            likeChecks[post.id] = liked;
          })
        );
        setLikedPosts((prev) => ({ ...prev, ...likeChecks }));
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [hasMore, loading, user, activePostTypeFilter]);

  /**
   * Set the active post type filter and refetch posts.
   * @param {string|null} typeFilter - 'text' | 'workout' | 'meal' | 'progress' | null
   */
  const setPostTypeFilter = useCallback((typeFilter) => {
    setActivePostTypeFilter(typeFilter);
    lastDocRef.current = null;
    fetchPosts(typeFilter);
  }, [fetchPosts]);

  /**
   * Create a new post and prepend it to the feed.
   * Supports all post types. Notifies registered feed listeners.
   */
  const createPost = useCallback(async ({
    type = 'text',
    text,
    imageFile,
    onImageProgress,
    workout,
    meal,
    progress,
    beforeImageFile,
    afterImageFile,
  }) => {
    setSubmitting(true);
    setError(null);
    try {
      const newPost = await postService.createPost({
        type,
        text,
        imageFile,
        author: {
          uid: user.uid,
          displayName: profile.displayName,
          username: profile.username,
          avatarUrl: profile.avatarUrl || null,
        },
        onImageProgress,
        workout,
        meal,
        progress,
        beforeImageFile,
        afterImageFile,
      });
      // Only prepend to context feed if no type filter is active, or the new post matches the filter
      if (!activePostTypeFilter || activePostTypeFilter === type) {
        setPosts((prev) => [newPost, ...prev]);
      }
      setLikedPosts((prev) => ({ ...prev, [newPost.id]: false }));

      // Notify feed listeners (Home/Explore pages)
      postCreatedListenersRef.current.forEach((listener) => {
        try {
          listener(newPost);
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[PostsContext] Listener error:', e);
          }
        }
      });

      setSubmitting(false);
      return newPost;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
      throw err;
    }
  }, [user, profile, activePostTypeFilter]);

  /**
   * Update a post in-place in the posts array.
   */
  const updatePost = useCallback(async (postId, data) => {
    setError(null);
    try {
      const updatedPost = await postService.updatePost(postId, data);
      setPosts((prev) =>
        prev.map((post) => (post.id === postId ? { ...post, ...updatedPost } : post))
      );
      return updatedPost;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a post from the posts array and Firestore.
   */
  const deletePost = useCallback(async (postId, imageUrl) => {
    setError(null);
    try {
      await postService.deletePost(postId, imageUrl);
      setPosts((prev) => prev.filter((post) => post.id !== postId));
      setLikedPosts((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Toggle like on a post with optimistic update.
   * Immediately updates UI, reverts on error.
   */
  const toggleLike = useCallback(async (postId) => {
    if (!user) return;

    const currentlyLiked = likedPosts[postId] || false;
    const delta = currentlyLiked ? -1 : 1;

    // Optimistic update
    setLikedPosts((prev) => ({ ...prev, [postId]: !currentlyLiked }));
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? { ...post, likesCount: Math.max(0, post.likesCount + delta) }
          : post
      )
    );

    try {
      if (currentlyLiked) {
        await postService.unlikePost(postId, user.uid);
      } else {
        await postService.likePost(postId, user.uid);
      }
    } catch (err) {
      // Revert optimistic update on error
      setLikedPosts((prev) => ({ ...prev, [postId]: currentlyLiked }));
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? { ...post, likesCount: Math.max(0, post.likesCount - delta) }
            : post
        )
      );
      setError(err.message);
    }
  }, [user, likedPosts]);

  // Initial fetch on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated, fetchPosts]);

  // Clear posts on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setPosts([]);
      setLikedPosts({});
      lastDocRef.current = null;
      setHasMore(true);
      setLoading(false);
      setError(null);
      setActivePostTypeFilter(null);
    }
  }, [isAuthenticated]);

  const value = {
    posts,
    loading,
    hasMore,
    error,
    submitting,
    likedPosts,
    activePostTypeFilter,
    // Text post dialog
    newPostOpen,
    openNewPost,
    closeNewPost,
    // Workout post dialog
    workoutPostOpen,
    openWorkoutPost,
    closeWorkoutPost,
    // Meal post dialog
    mealPostOpen,
    openMealPost,
    closeMealPost,
    // Progress post dialog
    progressPostOpen,
    openProgressPost,
    closeProgressPost,
    // Actions
    clearError,
    fetchPosts,
    fetchMorePosts,
    setPostTypeFilter,
    createPost,
    updatePost,
    deletePost,
    toggleLike,
    onPostCreated,
  };

  return (
    <PostsContext.Provider value={value}>
      {children}
    </PostsContext.Provider>
  );
};

export const usePosts = () => {
  const context = useContext(PostsContext);
  if (!context) {
    throw new Error('usePosts must be used within a PostsProvider');
  }
  return context;
};

export { PostsContext };
export default PostsProvider;
